import { Component, OnInit, OnDestroy, ViewChild, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { TableModule, Table } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';

export interface Project {
  id: number;
  name: string;
}

@Component({
  selector: 'app-projects',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ConfirmDialogModule,
    IconFieldModule,
    InputIconModule,
    SkeletonModule,
    ToastModule
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.css'
})
export class ProjectsComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table!: Table;
  
  // Signals
  projects = signal<Project[]>([]);
  loading = signal(true);
  totalRecords = signal(0);
  
  // Table config
  readonly rows = 10;
  first = 0;
  
  // Display data: empty rows during loading, real data otherwise
  displayData = computed(() => 
    this.loading() ? Array(this.rows).fill({}) : this.projects()
  );
  
  private timeout?: number;

  constructor(
    private router: Router,
    private confirmationService: ConfirmationService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  ngOnDestroy(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  private loadProjects(): void {
    if (this.timeout) clearTimeout(this.timeout);
    
    this.loading.set(true);
    this.projects.set([]);
    this.totalRecords.set(0);
    
    this.timeout = window.setTimeout(() => {
      const mockProjects = this.generateMockProjects();
      this.projects.set(mockProjects);
      this.totalRecords.set(mockProjects.length);
      this.loading.set(false);
    }, 2000);
  }

  private generateMockProjects(): Project[] {
    return [
      { id: 1, name: 'Website Redesign' },
      { id: 2, name: 'Mobile App Development' },
      { id: 3, name: 'Database Migration' },
      { id: 4, name: 'API Integration' },
      { id: 5, name: 'User Authentication System' },
      { id: 6, name: 'E-commerce Platform' },
      { id: 7, name: 'Analytics Dashboard' },
      { id: 8, name: 'Content Management System' },
      { id: 9, name: 'Payment Gateway Integration' },
      { id: 10, name: 'Performance Optimization' },
      { id: 11, name: 'Security Audit' },
      { id: 12, name: 'Third-party Integrations' },
      { id: 13, name: 'Automated Testing Suite' },
      { id: 14, name: 'Documentation Portal' },
      { id: 15, name: 'Backup & Recovery System' }
    ];
  }

  editProject(project: Project): void {
    this.router.navigate(['/projects', project.id]);
  }

  addProject(): void {
    this.router.navigate(['/projects', 1]);
  }

  deleteProject(project: Project): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('projects.confirmDelete.message', { name: project.name }),
      header: this.translateService.instant('projects.confirmDelete.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      acceptLabel: this.translateService.instant('projects.confirmDelete.confirm'),
      rejectLabel: this.translateService.instant('projects.confirmDelete.cancel'),
      accept: () => console.log('Project deleted:', project)
    });
  }
}